import {
    BuildOutlined,
    CalendarOutlined,
  DownloadOutlined,
  DownOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  LinkedinOutlined,
    MailOutlined,
    PhoneOutlined,
    StarOutlined,
    UpOutlined,
    UserOutlined
} from '@ant-design/icons';
import { Button, Card, Checkbox, Col, Divider, Modal, Row, Select, Space, Tag, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { contactsApi } from '../../services/contactsApi';

const { Title, Text } = Typography;

const CVModal = ({ cvData, isOpen, onClose, onSendCV, onScheduleInterview }) => {
  const navigate = useNavigate();
  const [isCvExpanded, setIsCvExpanded] = useState(false);
  const [formatting, setFormatting] = useState(false);
  const [selectedCvPdfB64, setSelectedCvPdfB64] = useState(null);
  const [anonymizeName, setAnonymizeName] = useState(true);
  const [templateStyle, setTemplateStyle] = useState('default');
  const [editableSkills, setEditableSkills] = useState([]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedCvPdfB64(null);
      setAnonymizeName(true);
      setTemplateStyle('default');
      setEditableSkills([]);
    }
  }, [isOpen]);

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short'
      });
    } catch {
      return dateString;
    }
  };

  // Helper function to parse work experience
  const parseWorkExperience = (workExp) => {
    if (!workExp || !Array.isArray(workExp)) return [];

    return workExp.map((exp, index) => ({
      id: index,
      title: exp.title || 'Unknown Title',
      company: exp.company || 'Unknown Company',
      location: exp.location || '',
      startDate: exp.starts_at || '',
      endDate: exp.ends_at || '',
      description: Array.isArray(exp.description) ? exp.description : [exp.description || '']
    }));
  };

  // Helper function to extract skills from text
  const extractSkills = (text) => {
    if (!text) return [];

    // Common skill keywords
    const skillKeywords = [
      'JavaScript', 'Python', 'Java', 'React', 'Node.js', 'SQL', 'AWS', 'Docker',
      'Kubernetes', 'Git', 'TypeScript', 'Angular', 'Vue.js', 'PHP', 'C++', 'C#',
      'Ruby', 'Go', 'Rust', 'Swift', 'Kotlin', 'Scala', 'R', 'MATLAB', 'HTML',
      'CSS', 'Sass', 'Less', 'Bootstrap', 'Tailwind', 'MongoDB', 'PostgreSQL',
      'MySQL', 'Redis', 'Elasticsearch', 'GraphQL', 'REST API', 'Microservices',
      'CI/CD', 'Agile', 'Scrum', 'DevOps', 'Machine Learning', 'AI', 'Data Science',
      'Analytics', 'Business Intelligence', 'Project Management', 'Leadership',
      'Communication', 'Problem Solving', 'Team Management', 'Strategic Planning'
    ];

    const foundSkills = skillKeywords.filter(skill =>
      text.toLowerCase().includes(skill.toLowerCase())
    );

    return foundSkills.slice(0, 10); // Limit to 10 skills
  };

  // Parse the CV data
  const contactName = cvData?.contactName || 'Unknown Contact';
  const title = cvData?.title || 'Unknown Title';
  const email = cvData?.email || '';
  const phone = cvData?.phone || '';
  const fullText = cvData?.text || '';
  const workExperience = parseWorkExperience(cvData?.workExperience);
  const extractedSkills = extractSkills(fullText);
  const linkedinUrl = cvData?.linkedinUrl || '';
  const rating = cvData?.rating || null;

  useEffect(() => {
    if (isOpen) {
      setEditableSkills(extractedSkills);
    }
  }, [isOpen, fullText]); // Keep editable list in sync with currently opened profile.

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <UserOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
          <span>CV - {contactName}</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      width={900}
      footer={null}
      bodyStyle={{ padding: '24px', maxHeight: '70vh', overflowY: 'auto' }}
    >
      {cvData && (
        <div className="space-y-6">
          {/* Header Section */}
          <Card className="cv-header-card">
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={16}>
                <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                  {contactName}
                </Title>
                <Title level={4} style={{ margin: '8px 0', color: '#666' }}>
                  {title}
                </Title>
                <Space wrap>
                  {rating && (
                    <Space>
                      <StarOutlined style={{ color: '#faad14' }} />
                      <Text style={{ color: '#faad14', fontWeight: 'bold' }}>
                        Rating: {rating.toFixed(1)}
                      </Text>
                    </Space>
                  )}
                  {email && (
                    <Space>
                      <MailOutlined style={{ color: '#1890ff' }} />
                      <Text copyable>{email}</Text>
                    </Space>
                  )}
                  {phone && (
                    <Space>
                      <PhoneOutlined style={{ color: '#1890ff' }} />
                      <Text copyable>{phone}</Text>
                    </Space>
                  )}
                  {linkedinUrl && (
                    <Space>
                      <LinkedinOutlined style={{ color: '#0077b5' }} />
                      <a href={linkedinUrl} target="_blank" rel="noopener noreferrer">
                        LinkedIn Profile
                      </a>
                    </Space>
                  )}
                </Space>
                {/* Actions */}
                <div style={{ marginTop: 12 }}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<MailOutlined />}
                      onClick={() => onSendCV && onSendCV(cvData)}
                    >
                      Send CV
                    </Button>
                    <Button
                      icon={<CalendarOutlined />}
                      onClick={() => onScheduleInterview && onScheduleInterview(cvData)}
                    >
                      Arrange Interview
                    </Button>
                  </Space>
                </div>
              </Col>
              <Col xs={24} sm={8} style={{ textAlign: 'right' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: '#1890ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto'
                }}>
                  <UserOutlined style={{ fontSize: '40px', color: 'white' }} />
                </div>
                <div style={{ marginTop: 12 }}>
                  <div style={{ marginBottom: 8 }}>
                    <Checkbox
                      checked={anonymizeName}
                      disabled
                    >
                      Anonymize candidate name (required)
                    </Checkbox>
                  </div>
                  <div style={{ marginBottom: 8, textAlign: 'left' }}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>CV Template</Text>
                    <Select
                      value={templateStyle}
                      onChange={setTemplateStyle}
                      style={{ width: '100%' }}
                      options={[
                        { value: 'default', label: 'Default' },
                        { value: 'executive', label: 'Executive' }
                      ]}
                    />
                  </div>
                  <Button
                    icon={<DownloadOutlined />}
                    loading={formatting}
                    onClick={async () => {
                      if (!cvData) return;
                      try {
                        setFormatting(true);
                        const payload = {
                          contactId: cvData.id,
                          contactName: cvData.contactName,
                          title: cvData.title,
                          fullText: cvData.text,
                          lastEvaluatedJob: cvData.lastEvaluatedJob || null,
                          evaluationSummary: cvData.evaluationSummary || null,
                          brand: {},
                          format: 'html-pdf',  // Use new HTML-to-PDF format with Playwright
                          anonymize: anonymizeName,  // Include anonymization setting
                          templateStyle,
                          skillsOverride: editableSkills
                        };
                        const resp = await contactsApi.formatCV(payload);
                        if (resp?.success && resp.pdfB64) {
                          // Save for later attachment in Send CV flow (include anonymization status)
                          setSelectedCvPdfB64(resp.pdfB64);
                          const link = document.createElement('a');
                          const styleSuffix = templateStyle === 'executive' ? '-executive' : '';
                          const filename = anonymizeName
                            ? `Confidential-Candidate${styleSuffix}.pdf`
                            : `${cvData.contactName || 'cv'}${styleSuffix}.pdf`;
                          link.href = `data:application/pdf;base64,${resp.pdfB64}`;
                          link.download = filename;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }
                      } catch (e) {
                        // best-effort UX; errors are shown elsewhere if needed
                      } finally {
                        setFormatting(false);
                      }
                    }}
                  >
                    Preview CV PDF
                  </Button>
                  <Button
                    icon={<FileTextOutlined />}
                    onClick={() => navigate('/candidate-profile-builder', {
                      state: {
                        backCandidateId: cvData?.id || cvData?.candidate_id || '',
                        cvData: {
                          ...cvData,
                          workExperience: cvData?.workExperience || cvData?.work_experience || cvData?.professionalExperience || [],
                          work_experience: cvData?.work_experience || cvData?.workExperience || [],
                          professionalExperience: cvData?.professionalExperience || cvData?.workExperience || [],
                          experience: cvData?.experience || cvData?.workExperience || [],
                          portraitUrl: cvData?.portraitUrl || cvData?.photoUrl || cvData?.photo_url || '',
                          portraitAlt: cvData?.portraitAlt || cvData?.contactName || '',
                          education: cvData?.education || cvData?.educationHistory || [],
                          educationHistory: cvData?.educationHistory || cvData?.education || [],
                          languages: cvData?.languages || cvData?.languageSkills || [],
                          systems: cvData?.systems || cvData?.tools || [],
                          skills: cvData?.skills || cvData?.ai_skills || [],
                          ai_skills: cvData?.ai_skills || cvData?.skills || [],
                          yearsOfExperience: cvData?.yearsOfExperience ?? cvData?.years_experience ?? undefined
                        }
                      }
                    })}
                  >
                    Open Builder
                  </Button>
                  {selectedCvPdfB64 && (
                    <div style={{ marginTop: 8 }}>
                      <Button type="primary" onClick={() => {
                        // When user chooses to send, pass attachment via onSendCV
                        const styleSuffix = templateStyle === 'executive' ? '-executive' : '';
                        const filename = anonymizeName
                          ? `Confidential-Candidate${styleSuffix}.pdf`
                          : `${cvData.contactName || 'cv'}${styleSuffix}.pdf`;
                        onSendCV && onSendCV({
                          ...cvData,
                          attachmentPdfB64: selectedCvPdfB64,
                          attachmentName: filename,
                          anonymized: anonymizeName,  // Track anonymization status
                          templateStyle
                        });
                      }}>Use this CV</Button>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Card>

          {/* Skills Section */}
          <Card title={<><StarOutlined /> Skills & Expertise (Editable Before Preview)</>} size="small">
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Add or remove skill tags"
              value={editableSkills}
              onChange={setEditableSkills}
              tokenSeparators={[',']}
              maxTagCount="responsive"
              options={extractedSkills.map((skill) => ({ value: skill, label: skill }))}
            />
            {editableSkills.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: 12 }}>
                {editableSkills.map((skill, index) => (
                  <Tag key={`${skill}-${index}`} color="blue" style={{ margin: '4px' }}>
                    {skill}
                  </Tag>
                ))}
              </div>
            )}
          </Card>

          {/* Suggested Skills Snapshot */}
          {extractedSkills.length > 0 && (
            <Card title={<><StarOutlined /> Skills & Expertise</>} size="small">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {extractedSkills.map((skill, index) => (
                  <Tag key={index} color="blue" style={{ margin: '4px' }}>
                    {skill}
                  </Tag>
                ))}
              </div>
            </Card>
          )}

          {/* Work Experience Section */}
          {workExperience.length > 0 && (
            <Card title={<><BuildOutlined /> Work Experience</>} size="small">
              <div className="space-y-4">
                {workExperience.map((exp, index) => (
                  <div key={exp.id} className="work-experience-item">
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px'
                    }}>
                      <div>
                        <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
                          {exp.title}
                        </Title>
                        <Text strong style={{ color: '#666' }}>
                          {exp.company}
                        </Text>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          <CalendarOutlined /> {formatDate(exp.startDate)} - {formatDate(exp.endDate)}
                        </div>
                        {exp.location && (
                          <div style={{ fontSize: '12px', color: '#999' }}>
                            <EnvironmentOutlined /> {exp.location}
                          </div>
                        )}
                      </div>
                    </div>
                    {exp.description && exp.description.length > 0 && (
                      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        {exp.description.map((desc, descIndex) => (
                          <li key={descIndex} style={{ marginBottom: '4px' }}>
                            <Text>{desc}</Text>
                          </li>
                        ))}
                      </ul>
                    )}
                    {index < workExperience.length - 1 && <Divider style={{ margin: '16px 0' }} />}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Full CV Content Section */}
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Full CV Content</span>
                <Button
                  type="text"
                  size="small"
                  icon={isCvExpanded ? <UpOutlined /> : <DownOutlined />}
                  onClick={() => setIsCvExpanded(!isCvExpanded)}
                  style={{ padding: '4px 8px' }}
                >
                  {isCvExpanded ? 'Collapse' : 'Expand'}
                </Button>
              </div>
            }
            size="small"
          >
            <div
              className="cv-content-scroll"
              style={{
                backgroundColor: '#f8f9fa',
                padding: '16px',
                borderRadius: '8px',
                fontFamily: 'monospace',
                fontSize: '14px',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
                maxHeight: isCvExpanded ? 'none' : '300px',
                overflowY: isCvExpanded ? 'visible' : 'auto',
                transition: 'max-height 0.3s ease-in-out'
              }}
            >
              {fullText || 'No CV content available'}
            </div>
          </Card>
        </div>
      )}
    </Modal>
  );
};

export default CVModal;
